import { normalizeCpf } from "src-next/shared/utils/cpf";
import type { MessagingPatient } from "src-next/modules/crm-clinical/messaging/types";
import { isVetApp } from "../../messaging/helpers/isVetApp";

import type { CrmPatientSummary } from "../types";
import { isLogicalCpfPatientId } from "./crmVetTutorRecipient";
import { parseFluxZapRecipientReference } from "./fluxZapRecipientReference";

function bareRecipientId(patientId: string): string {
  const parsed = parseFluxZapRecipientReference(patientId);
  if (parsed) return parsed.id;
  return String(patientId ?? "").trim();
}

/**
 * Converte resumo CRM (PatientOrganizationContext / agregações) para o contrato
 * de envio do módulo messaging (`SendWhatsAppMessageDialog` / FluxZap).
 */
export function mapCrmPatientSummaryToMessagingPatient(
  patient: CrmPatientSummary,
): MessagingPatient {
  const patientId = bareRecipientId(String(patient.patientId ?? "").trim());
  const cpfRaw = String(patient.taxIdentifierValue ?? "").trim();
  const cpfDigits = cpfRaw ? normalizeCpf(cpfRaw) : "";

  const vetTutorRecipient = isVetApp() && isLogicalCpfPatientId(patientId);
  const isRegisteredClient = patient.recipientResourceType === "Person";

  return {
    id: patientId,
    fullName:
      String(patient.usualName ?? "").trim() ||
      String(patient.officialName ?? "").trim() ||
      patientId,
    cpf: cpfDigits.length === 11 ? cpfDigits : cpfRaw || undefined,
    phone: patient.phone?.trim() || undefined,
    birthDate: patient.birthDate?.trim() || undefined,
    /**
     * Cliente cadastrado: Person/{id}. Tutor vet: CPF. Demais: Patient/{id}.
     */
    forReference: vetTutorRecipient
      ? undefined
      : isRegisteredClient && patientId
        ? `Person/${patientId}`
        : patientId
          ? `Patient/${patientId}`
          : undefined,
    isTutor: vetTutorRecipient ? true : undefined,
  };
}

/** Task de questionário só em Patient — nunca Person/. */
export function taskPatientReferenceFromCrmPatient(
  patient: CrmPatientSummary,
): string | null {
  if (patient.recipientResourceType === "Person") return null;
  const id = bareRecipientId(String(patient.patientId ?? "").trim());
  if (!id) return null;
  return `Patient/${id}`;
}

export function lookupRecipientSummary(
  rawId: string,
  lookup: Map<string, CrmPatientSummary>,
): CrmPatientSummary | undefined {
  const trimmed = String(rawId ?? "").trim();
  if (!trimmed) return undefined;
  const direct = lookup.get(trimmed);
  if (direct) return direct;
  const parsed = parseFluxZapRecipientReference(trimmed);
  if (parsed) {
    return lookup.get(parsed.reference) ?? lookup.get(parsed.id);
  }
  return lookup.get(`Patient/${trimmed}`) ?? lookup.get(`Person/${trimmed}`);
}

/**
 * Resolve destinatários a partir de IDs/referências do extrato.
 * Person sem match não vira Patient.
 */
export function crmPatientsFromIds(
  ids: Iterable<string>,
  lookup: Map<string, CrmPatientSummary>,
): CrmPatientSummary[] {
  const result: CrmPatientSummary[] = [];
  for (const id of ids) {
    const trimmed = String(id ?? "").trim();
    if (!trimmed) continue;
    const found = lookupRecipientSummary(trimmed, lookup);
    if (found) {
      result.push(found);
      continue;
    }
    const parsed = parseFluxZapRecipientReference(trimmed);
    if (parsed?.resourceType === "Person") {
      result.push({
        patientId: parsed.id,
        officialName: "Cliente cadastrado",
        birthDate: "",
        recipientResourceType: "Person",
      });
      continue;
    }
    result.push({
      patientId: parsed?.id ?? trimmed,
      officialName: parsed?.id ?? trimmed,
      birthDate: "",
    });
  }
  return result;
}
